import {
  AccountOwnerDetailResponse,
  VirtualAccountTransactionResponse,
} from '@b7hio/api-lib/src/ops-portal';
import { FormFriendly, superstructResolver } from '@b7hio/core-lib/src/form';
import { useTranslation } from 'react-i18next';
import { NextSeo } from 'next-seo';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { create } from 'superstruct';
import { useDebouncedCallback } from 'use-debounce';
import { getSessionValue, PageHeader } from '../../components';
import { VirtualAccountTransactionList } from './components';
import {
  initialValues,
  VirtualTransactionListFilterValues,
  virtualTransactionsListFilterValidation,
} from './components/VirtualTransactionListFilter/VirtualTransactionListFilter.validation';

interface Props {
  readonly isLoading: boolean;
  readonly data: readonly VirtualAccountTransactionResponse[];
  readonly initialFilter?: VirtualTransactionListFilterValues;
  readonly onFilterChange: (value: VirtualTransactionListFilterValues) => void;
  readonly banksList: readonly AccountOwnerDetailResponse[];
  readonly customerList: readonly AccountOwnerDetailResponse[];
  readonly platformList: readonly AccountOwnerDetailResponse[];
}

export const VirtualTransactionListPage: React.FC<Props> = ({
  isLoading,
  data,
  onFilterChange,
  banksList,
  customerList,
  platformList,
}) => {
  const { t } = useTranslation(['common', 'transactions']);

  const methods = useForm<FormFriendly<VirtualTransactionListFilterValues>>({
    mode: 'onChange',
    resolver: superstructResolver(virtualTransactionsListFilterValidation, {
      coerce: true,
    }),
    defaultValues: getSessionValue<VirtualTransactionListFilterValues>('virtual_transactions_list_filter', initialValues),
  });

  const submitForm = useDebouncedCallback(
    async (values: FormFriendly<VirtualTransactionListFilterValues>) => {
      await onFilterChange(
        create(values, virtualTransactionsListFilterValidation)
      );
    },
    200
  );

  // Form Listener
  useEffect(() => {
    const subscription = methods.watch(submitForm);
    return () => subscription.unsubscribe();
  }, [methods.watch, onFilterChange, submitForm]);

  return (
    <>
      <NextSeo title={t('transactions:page.virtualAccountTitle')} />
      <PageHeader title={t('transactions:page.virtualAccountTitle')} />
      <FormProvider {...methods}>
        <VirtualAccountTransactionList
          isLoading={isLoading}
          data={data}
          banksList={banksList}
          customerList={customerList}
          platformList={platformList}
        />
      </FormProvider>
    </>
  );
};
